// Today's Breaking News – 10 articles dated today
import type { RawArticle } from "./articles";

import heroTanker from "@/assets/hero-tanker.jpg";
import refinery from "@/assets/refinery.jpg";
import shippingPort from "@/assets/shipping-port.jpg";
import lngTanker from "@/assets/lng-tanker.jpg";
import tradingFloor from "@/assets/trading-floor.jpg";
import renewables from "@/assets/renewables.jpg";
import pipeline from "@/assets/pipeline.jpg";

import gasPlant from "@/assets/gas-plant.jpg";
import euPolicy from "@/assets/eu-policy.jpg";

const today = new Date().toISOString().split("T")[0];

export const draftArticlesToday: RawArticle[] = [
  {
    id: "today-2",
    slug: "maersk-raises-full-year-guidance-strong-container-demand",
    title: "Maersk Raises Full-Year Guidance as Container Demand Exceeds Expectations",
    summary: "AP Moller-Maersk upgraded its 2026 earnings forecast citing stronger-than-expected container volumes on trans-Pacific and Asia-Europe routes.",
    content: `AP Moller-Maersk, the world's second-largest container shipping line, raised its full-year earnings guidance on Wednesday after reporting robust first-quarter volumes that exceeded analyst expectations.

The Danish shipping giant now expects underlying EBITDA of $9–10 billion for 2026, up from a previous range of $7.5–9 billion. The upgrade reflects stronger container throughput on key east-west trade lanes and higher average freight rates compared to the depressed levels seen in late 2025.

**Key details**

Maersk reported that loaded container volumes rose 8% year-on-year in Q1 2026, driven by restocking activity in the United States and resilient European import demand. Trans-Pacific volumes were particularly strong, with spot rates on the Shanghai-Los Angeles corridor averaging $3,200 per forty-foot equivalent unit (FEU)—well above the sub-$2,000 levels that prevailed a year earlier.

CEO Vincent Clerc attributed the improvement to "a more balanced supply-demand environment" following industry-wide capacity discipline. The orderbook for new container vessels has moderated after a years-long building spree, easing concerns about structural overcapacity.

**Market impact**

Maersk shares rose 6% in Copenhagen trading following the announcement. Shares of rivals Hapag-Lloyd and ZIM Integrated Shipping also gained on the news, as investors bet the demand improvement would benefit the broader liner sector.

Freight rate benchmarks published by Drewry and the Shanghai Containerized Freight Index have shown steady gains since January, with the composite index up approximately 25% year-to-date.

**Context & outlook**

Analysts caution that the demand recovery could face headwinds in the second half if trade policy uncertainty resurfaces. US tariff reviews scheduled for mid-2026 could dampen trans-Pacific trade flows if additional duties are imposed on Chinese goods.

Maersk's logistics division also showed improvement, with warehousing utilization rates climbing to 85% from 78% a year earlier.`,
    author: "Henrik Larsen",
    publicationDate: today,
    category: "Shipping",
    tags: ["Maersk", "container shipping", "freight rates", "guidance upgrade", "trans-Pacific"],
    imageUrl: shippingPort,
    imageAlt: "Container port with cranes loading vessels representing global trade",
    isOpinion: false,
    isFeature: true,
    readTime: 5,
    keyPoints: [
      "Maersk raises 2026 EBITDA guidance to $9–10 billion from $7.5–9 billion",
      "Container volumes up 8% YoY in Q1 on strong trans-Pacific demand",
      "Shanghai-LA spot rates averaging $3,200/FEU vs sub-$2,000 a year ago",
      "Shares up 6%; positive read-through for broader liner sector",
    ],
    sources: [
      { name: "Maersk Investor Relations", url: "https://www.maersk.com/investors" },
      { name: "Drewry Shipping Consultants", url: "https://www.drewry.co.uk" },
    ],
  },
  {
    id: "today-3",
    slug: "fed-signals-rate-hold-through-june-inflation-sticky",
    title: "Federal Reserve Signals Rate Hold Through June as Inflation Remains Sticky",
    summary: "Fed officials indicated interest rates will remain unchanged at 4.25–4.50% through at least mid-2026, citing persistent services inflation and a resilient labor market.",
    content: `The US Federal Reserve signaled on Wednesday that it expects to hold its benchmark interest rate steady at the 4.25–4.50% range through at least June 2026, as policymakers grapple with inflation that has proven more persistent than anticipated.

Minutes from the January Federal Open Market Committee meeting, released today, revealed that "nearly all participants" agreed the current policy stance was appropriate given the balance of risks to the economic outlook.

**Key details**

Core PCE inflation—the Fed's preferred measure—stood at 2.8% in January, above the central bank's 2% target and essentially unchanged from three months earlier. Services inflation, particularly in housing and healthcare, continued to run above 4%, offsetting declines in goods prices.

The labor market showed no signs of material cooling, with nonfarm payrolls averaging 195,000 per month over the past quarter and the unemployment rate holding at 3.9%.

"The committee remains attentive to inflation risks and is prepared to maintain the current policy stance for as long as necessary," stated Fed Chair Jerome Powell in prepared remarks following the minutes' release.

**Market impact**

Treasury yields rose modestly, with the 2-year note climbing 5 basis points to 4.38%. The US dollar strengthened against major currencies, while equity markets dipped as rate-cut expectations were pushed further out.

Fed funds futures now imply roughly a 30% probability of a rate cut by September 2026, down from 55% a month ago. The shift has implications for commodity markets, as a stronger dollar typically weighs on dollar-denominated raw materials.

**Context & outlook**

The Fed's cautious stance contrasts with the European Central Bank, which has already delivered two 25-basis-point cuts this year. The policy divergence has pushed EUR/USD below 1.04, its lowest level since 2023.

Market participants will focus on the February jobs report and the March CPI print for further clarity on the rate path.`,
    author: "Elena Vasquez",
    publicationDate: today,
    category: "Banking & Finance",
    tags: ["Federal Reserve", "interest rates", "inflation", "monetary policy", "Treasury yields"],
    imageUrl: tradingFloor,
    imageAlt: "Financial trading screens showing market data and interest rate indicators",
    isOpinion: false,
    isFeature: true,
    readTime: 6,
    keyPoints: [
      "Fed holds rates at 4.25–4.50%, signals no cuts through at least June 2026",
      "Core PCE inflation stuck at 2.8%, well above 2% target",
      "Rate-cut probability for September drops to 30% from 55%",
      "US dollar strengthens; EUR/USD slides below 1.04",
      "Contrasts with ECB, which has already cut twice in 2026",
    ],
    sources: [
      { name: "Federal Reserve", url: "https://www.federalreserve.gov/monetarypolicy.htm" },
      { name: "Bureau of Economic Analysis", url: "https://www.bea.gov" },
    ],
  },
  {
    id: "today-4",
    slug: "eu-carbon-border-tax-hits-first-steel-imports",
    title: "EU Carbon Border Tax Hits First Steel Imports as CBAM Enters Definitive Phase",
    summary: "The European Union's Carbon Border Adjustment Mechanism begins collecting levies on imported steel, cement, and aluminum, marking a watershed moment for global trade policy.",
    content: `The European Union's Carbon Border Adjustment Mechanism entered its definitive phase on Wednesday, with importers of steel, cement, aluminum, fertilizers, and electricity now required to purchase CBAM certificates reflecting the carbon intensity of their products.

The landmark regulation—the first carbon border tax of its kind globally—aims to prevent "carbon leakage" by ensuring that imported goods face comparable carbon costs to those produced within the EU's Emissions Trading System.

**Key details**

Under the new rules, EU importers must surrender CBAM certificates corresponding to the embedded emissions in their goods. Certificate prices are linked to the EU ETS carbon price, which currently trades near €72 per tonne of CO₂.

The European Commission estimates the mechanism will cover approximately €3.5 billion in annual imports during its initial phase. Steel imports from Turkey, India, and China are expected to bear the largest cost burden.

"CBAM is a critical tool to ensure our climate ambitions do not come at the expense of European industrial competitiveness," stated EU Climate Commissioner Wopke Hoekstra.

**Market impact**

European steel producers rallied on the news, with ArcelorMittal shares gaining 3.2% and Thyssenkrupp up 2.8%. The policy effectively narrows the cost advantage that foreign producers enjoyed by operating under less stringent emissions regulations.

Indian steel exporters, however, warned of significant impacts. The Indian Steel Association estimated that CBAM levies could add $30–50 per tonne to the cost of Indian steel exports to the EU.

**Context & outlook**

Several trading partners have signaled potential retaliatory measures. Turkey has filed a formal complaint with the World Trade Organization, arguing that CBAM constitutes a discriminatory trade barrier.

The EU plans to expand CBAM coverage to additional product categories, including organic chemicals and polymers, by 2028.`,
    author: "Thomas Bergström",
    publicationDate: today,
    category: "Policy & Regulations",
    tags: ["CBAM", "EU", "carbon tax", "steel", "trade policy", "climate"],
    imageUrl: euPolicy,
    imageAlt: "European Union flags representing climate and trade policy",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "CBAM definitive phase begins—importers must buy certificates linked to EU ETS carbon price (~€72/t)",
      "Covers steel, cement, aluminum, fertilizers, and electricity imports",
      "European steel stocks rally; Indian exporters face $30–50/t cost increase",
      "Turkey files WTO complaint; expansion to chemicals and polymers planned by 2028",
    ],
    sources: [
      { name: "European Commission – CBAM", url: "https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism_en" },
      { name: "EU Emissions Trading System", url: "https://climate.ec.europa.eu/eu-action/eu-emissions-trading-system-eu-ets_en" },
    ],
  },
  {
    id: "today-5",
    slug: "china-refinery-throughput-hits-record-february-2026",
    title: "China's Refinery Throughput Hits Record High as Domestic Demand Recovers",
    summary: "Chinese refineries processed a record 15.2 million barrels per day in January, driven by recovering petrochemical demand and increased aviation fuel consumption.",
    content: `China's refinery throughput surged to a record 15.2 million barrels per day in January 2026, according to data released by the National Bureau of Statistics on Wednesday, signaling a robust recovery in the world's largest crude oil importing nation.

The figure represents a 4.3% increase compared to the same month last year and surpasses the previous record of 14.9 million bpd set in June 2025.

**Key details**

The throughput surge was driven by several factors. Petrochemical demand—particularly for ethylene and propylene feedstocks—expanded as downstream manufacturing activity accelerated ahead of the Lunar New Year. Aviation fuel consumption also rose sharply, with domestic passenger flights up 12% year-on-year.

Independent "teapot" refineries in Shandong province operated at near-capacity utilization rates of 78%, their highest since mid-2024, as improved margins incentivized maximum output.

China's crude oil imports totaled 11.8 million bpd in January, with increased purchases from Russia, Iran, and Brazil partially offsetting reduced Saudi volumes.

**Market impact**

The strong throughput data provided support to global crude benchmarks. Brent crude's prompt spread widened into steeper backwardation, a sign of tightening near-term supply.

Asian refining margins for gasoline and naphtha also strengthened, with the Singapore gasoline crack rising to $12 per barrel—its highest seasonal level in three years.

**Context & outlook**

Analysts caution that February throughput may ease from January's highs due to refinery maintenance schedules and the typical post-holiday demand dip. However, the underlying trend points to China reasserting its role as the primary driver of global oil demand growth.

The IEA now projects Chinese oil demand to grow by 400,000 bpd in 2026, revised upward from a previous estimate of 300,000 bpd.`,
    author: "Michael Chen",
    publicationDate: today,
    category: "Market Analysis",
    tags: ["China", "refinery", "crude oil", "demand", "petrochemicals"],
    imageUrl: refinery,
    imageAlt: "Large oil refinery complex representing Chinese processing capacity",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "Chinese refinery throughput hit record 15.2 million bpd in January 2026",
      "Petrochemical and aviation fuel demand drove the surge",
      "Crude imports totaled 11.8 million bpd; Russia, Iran, Brazil volumes up",
      "IEA revises China demand growth forecast upward to 400,000 bpd for 2026",
    ],
    sources: [
      { name: "China National Bureau of Statistics", url: "http://www.stats.gov.cn/english/" },
      { name: "IEA Oil Market Report", url: "https://www.iea.org/reports/oil-market-report-february-2026" },
    ],
  },
  {
    id: "today-6",
    slug: "suez-canal-transits-rebound-houthi-ceasefire-holds",
    title: "Suez Canal Transits Rebound as Red Sea Ceasefire Holds into Third Week",
    summary: "Daily transits through the Suez Canal have risen 40% since the Houthi ceasefire, with major shipping lines cautiously resuming Red Sea routings.",
    content: `Daily transits through the Suez Canal have increased approximately 40% over the past three weeks following a ceasefire agreement with Houthi forces in Yemen, according to data from the Suez Canal Authority released on Wednesday.

The rebound represents a significant shift after more than a year of disrupted trade flows through one of the world's most critical maritime chokepoints.

**Key details**

Average daily transits have risen to 58 vessels from approximately 41 during the peak of the Red Sea crisis, though they remain below the pre-crisis average of 75 vessels per day. The Suez Canal Authority reported that toll revenues have recovered to approximately $650 million per month, compared to a low of $400 million during the worst months of diversions.

Maersk, MSC, and CMA CGM—the world's three largest container lines—have begun cautiously routing select services through the canal rather than the longer Cape of Good Hope route. However, all three have maintained contingency plans for rapid re-routing should the security situation deteriorate.

**Market impact**

The resumption of Suez transits has begun to ease pressure on shipping capacity. Container freight rates on the Asia-Europe route have declined approximately 15% since the ceasefire, with the Shanghai-Rotterdam benchmark falling to $2,400/FEU from $2,800/FEU.

Tanker rates have also softened, particularly for Suezmax vessels that had benefited from the longer voyage distances via the Cape.

**Context & outlook**

Despite the improvement, industry stakeholders remain cautious. War risk insurance premiums for Red Sea transits remain elevated at approximately 0.5% of hull value, compared to pre-crisis levels below 0.1%.

The ceasefire's durability will be tested in the coming weeks as diplomatic negotiations continue. Any resumption of attacks would likely trigger an immediate return to diversionary routing.`,
    author: "Henrik Larsen",
    publicationDate: today,
    category: "Shipping",
    tags: ["Suez Canal", "Red Sea", "Houthi", "shipping", "freight rates"],
    imageUrl: lngTanker,
    imageAlt: "Large vessel transiting a major shipping lane",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "Suez Canal daily transits up 40% to 58 vessels since Houthi ceasefire",
      "Major container lines cautiously resuming Red Sea routing",
      "Asia-Europe container rates down 15% to $2,400/FEU",
      "War risk insurance still elevated at 0.5% of hull value",
    ],
    sources: [
      { name: "Suez Canal Authority", url: "https://www.suezcanal.gov.eg" },
      { name: "Drewry Shipping Consultants", url: "https://www.drewry.co.uk" },
    ],
  },
  {
    id: "today-7",
    slug: "goldman-sachs-commodity-supercycle-copper-gold-forecast",
    title: "Goldman Sachs Doubles Down on Commodity Supercycle Call, Raises Copper and Gold Targets",
    summary: "Goldman Sachs reiterated its structural bull case for commodities, raising its 12-month copper target to $12,000/t and gold to $5,500/oz citing electrification demand and central bank buying.",
    content: `Goldman Sachs reinforced its commodity supercycle thesis on Wednesday, raising price targets for copper and gold while maintaining an overweight recommendation on the broader commodity complex.

The bank's research team set a 12-month copper target of $12,000 per tonne, up from a previous $10,500 forecast, and raised its gold target to $5,500 per ounce from $4,800.

**Key details**

The bullish copper call is predicated on accelerating demand from electrification, data center construction, and electric vehicle production, combined with constrained mine supply. Goldman estimates the global copper market will face a deficit of approximately 500,000 tonnes in 2026, widening to 800,000 tonnes by 2028.

"Copper is the most critical metal for the energy transition, and supply is simply not keeping pace," wrote Jeffrey Currie, Goldman's head of commodities research. "We see structural deficits as far as the eye can see."

On gold, the bank cited continued central bank purchasing—particularly from China, India, and Middle Eastern sovereigns—as well as renewed interest from Western institutional investors seeking inflation hedges.

**Market impact**

Copper futures on the LME rose 1.8% to $5,850 per tonne following the note, while gold held near record highs above $5,030 per ounce. Mining stocks rallied, with Freeport-McMoRan up 4% and Newmont gaining 2.5%.

The Goldman note also highlighted natural gas as a "bridging fuel" opportunity, raising its Henry Hub forecast to $3.50/MMBtu for late 2026 on growing LNG export demand and AI-driven data center power consumption.

**Context & outlook**

Not all analysts share Goldman's enthusiasm. Citi's commodity team maintained a more cautious outlook, arguing that Chinese property weakness and potential tariff escalation could cap commodity upside in the near term.`,
    author: "Grace Wong",
    publicationDate: today,
    category: "Market Analysis",
    tags: ["Goldman Sachs", "copper", "gold", "supercycle", "commodities forecast"],
    imageUrl: tradingFloor,
    imageAlt: "Commodity trading screens displaying metals and energy prices",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "Goldman raises 12-month copper target to $12,000/t on electrification demand",
      "Gold target lifted to $5,500/oz; central bank buying remains key driver",
      "Global copper deficit projected at 500,000t in 2026, widening to 800,000t by 2028",
      "Mining stocks rally; Freeport-McMoRan +4%, Newmont +2.5%",
    ],
    sources: [
      { name: "Goldman Sachs Commodities Research", url: "https://www.goldmansachs.com/insights/topics/commodities.html" },
      { name: "London Metal Exchange", url: "https://www.lme.com" },
    ],
  },
  {
    id: "today-8",
    slug: "india-solar-capacity-crosses-100gw-milestone",
    title: "India Crosses 100 GW Solar Capacity Milestone, Eyes 300 GW by 2030",
    summary: "India became the third country after China and the US to surpass 100 GW of installed solar capacity, as the government reaffirms its ambitious renewable energy targets.",
    content: `India officially crossed the 100-gigawatt milestone for installed solar power capacity on Wednesday, cementing its position as the world's third-largest solar market behind China and the United States.

The milestone was confirmed by the Ministry of New and Renewable Energy, which reported that cumulative solar installations reached 101.4 GW as of February 15, 2026.

**Key details**

India added approximately 22 GW of new solar capacity in 2025, a record annual installation driven by large-scale utility projects in Rajasthan, Gujarat, and Karnataka. The government's production-linked incentive scheme for domestic solar module manufacturing has also begun to bear fruit, with Indian manufacturers now supplying roughly 40% of domestic panel demand, up from 15% three years ago.

"This milestone reflects India's unwavering commitment to clean energy leadership," stated Minister of Power and New Renewable Energy R.K. Singh. "Our target of 300 GW solar by 2030 is ambitious but achievable."

The 100 GW achievement means solar now accounts for approximately 23% of India's total installed power generation capacity, though its share of actual electricity generation is lower at around 12% due to capacity factor differences.

**Market impact**

Indian renewable energy stocks gained on the news, with Adani Green Energy up 3.5% and Tata Power rising 2.1%. Solar module prices in India have stabilized around $0.22/watt after declining steadily over the past two years.

**Context & outlook**

Challenges remain. Grid integration of variable renewable energy continues to strain India's transmission infrastructure, and energy storage deployment—critical for managing solar intermittency—lags behind installation rates.

India aims for 500 GW of total non-fossil fuel capacity by 2030 under its Nationally Determined Contributions to the Paris Agreement.`,
    author: "James Okafor",
    publicationDate: today,
    category: "Energy & Commodities",
    tags: ["India", "solar energy", "renewables", "energy transition", "clean energy"],
    imageUrl: renewables,
    imageAlt: "Solar panels and wind turbines representing renewable energy growth",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "India crosses 100 GW installed solar capacity—third country globally to do so",
      "Record 22 GW added in 2025; domestic manufacturing share rises to 40%",
      "Government targets 300 GW solar and 500 GW non-fossil fuel by 2030",
      "Grid integration and energy storage remain key challenges",
    ],
    sources: [
      { name: "India Ministry of New and Renewable Energy", url: "https://mnre.gov.in" },
      { name: "International Solar Alliance", url: "https://isolaralliance.org" },
    ],
  },
  {
    id: "today-9",
    slug: "lng-spot-prices-surge-europe-cold-snap-storage-drawdown",
    title: "LNG Spot Prices Surge as European Cold Snap Accelerates Storage Drawdown",
    summary: "Asian and European LNG spot prices jumped to $14/MMBtu as an unexpected late-winter cold snap across Northern Europe drives rapid gas storage withdrawals.",
    content: `Spot liquefied natural gas prices surged on Wednesday, with the JKM Asian benchmark and European TTF both climbing above $14 per million British thermal units as an unexpected cold snap across Northern Europe accelerated gas storage drawdowns.

The price spike marks a 20% increase over the past week and represents the highest spot LNG levels since November 2025.

**Key details**

Temperatures across Scandinavia, the UK, and Germany dropped 8–10°C below seasonal averages this week, driving a sharp increase in heating demand. European gas storage levels, which had been tracking comfortably above the five-year average, fell to 38% of capacity—roughly 5 percentage points below where they stood at this time last year.

"The market was positioned for an uneventful end to winter," said Nils Andersson, senior gas analyst at Nordic Energy Research. "This cold snap has caught many off guard and reminded traders that weather risk is never fully priced out."

European utilities have stepped up spot LNG purchases, competing with Asian buyers who are restocking ahead of the spring maintenance season. At least six spot cargoes originally destined for Asia were diverted to European terminals this week.

**Market impact**

The TTF front-month contract settled at €43/MWh, up from €36/MWh a week ago. UK NBP prices rose in lockstep. European gas equities rallied, with Equinor gaining 2.3% and Shell's integrated gas division contributing to a 1.5% gain in the company's share price.

**Context & outlook**

The supply tightness is expected to persist through early March if cold weather continues. However, the onset of spring maintenance at Norwegian gas fields could constrain pipeline supply, keeping prices elevated even if temperatures moderate.

LNG export capacity from the US Gulf Coast is running at near-maximum rates, with Sabine Pass and Freeport LNG operating at full throughput.`,
    author: "Thomas Bergström",
    publicationDate: today,
    category: "Energy & Commodities",
    tags: ["LNG", "natural gas", "Europe", "cold snap", "TTF", "energy prices"],
    imageUrl: gasPlant,
    imageAlt: "LNG processing facility representing global gas trade",
    isOpinion: false,
    isFeature: false,
    readTime: 5,
    keyPoints: [
      "JKM and TTF LNG spot prices surge above $14/MMBtu—highest since November 2025",
      "Northern European temperatures 8–10°C below seasonal norms",
      "EU gas storage falls to 38% of capacity, 5pp below year-ago levels",
      "Six spot LNG cargoes diverted from Asia to Europe this week",
    ],
    sources: [
      { name: "Gas Infrastructure Europe (GIE)", url: "https://www.gie.eu" },
      { name: "ICE Futures Europe", url: "https://www.theice.com/products/27996665/Dutch-TTF-Gas-Futures" },
    ],
  },
  {
    id: "today-10",
    slug: "sec-proposes-stricter-esg-disclosure-rules-commodity-firms",
    title: "SEC Proposes Stricter ESG Disclosure Rules for Commodity and Energy Firms",
    summary: "The US Securities and Exchange Commission unveiled proposed rules requiring listed energy and commodity companies to provide standardized climate risk and emissions disclosures.",
    content: `The US Securities and Exchange Commission on Wednesday proposed new rules that would require publicly listed energy and commodity companies to provide standardized, auditable disclosures on climate-related risks, greenhouse gas emissions, and transition plans.

The proposal marks a significant expansion of the SEC's earlier climate disclosure framework, which had been scaled back in 2024 following legal challenges from industry groups.

**Key details**

Under the proposed rules, companies with annual revenues exceeding $1 billion would be required to disclose Scope 1 (direct) and Scope 2 (energy-related) emissions with third-party assurance. Companies would also need to provide narrative descriptions of how climate risks—including physical risks and transition risks—could materially affect their business.

The proposal stops short of mandating Scope 3 (supply chain) emissions reporting but requires companies to describe their approach to Scope 3 measurement "if material."

SEC Chair Gary Gensler stated: "Investors need consistent, comparable, and reliable information about how companies are managing climate-related risks. These rules deliver on that need."

**Market impact**

Energy sector stocks dipped modestly on the news, with the S&P 500 Energy Index falling 0.4%. Compliance cost estimates range from $500,000 to $5 million annually per large company, depending on the complexity of operations.

ESG-focused investment firms welcomed the proposal. BlackRock's head of sustainable investing called it "a step toward the transparency that capital markets require."

**Context & outlook**

The proposed rules face a 90-day public comment period and potential legal challenges from industry groups. The American Petroleum Institute has already signaled opposition, arguing that existing voluntary frameworks are sufficient.

If finalized, the rules would take effect for fiscal years beginning after December 2027, giving companies roughly 18 months to build reporting infrastructure.

The proposal aligns the US more closely with the EU's Corporate Sustainability Reporting Directive, which already requires detailed ESG disclosures from large European companies.`,
    author: "Elena Vasquez",
    publicationDate: today,
    category: "Policy & Regulations",
    tags: ["SEC", "ESG", "climate disclosure", "regulation", "energy sector"],
    imageUrl: pipeline,
    imageAlt: "Energy infrastructure representing corporate climate risk reporting",
    isOpinion: false,
    isFeature: false,
    readTime: 6,
    keyPoints: [
      "SEC proposes mandatory Scope 1 & 2 emissions disclosures with third-party assurance",
      "Applies to listed energy/commodity firms with >$1B annual revenue",
      "Scope 3 reporting encouraged but not mandated; 90-day comment period begins",
      "Compliance costs estimated at $500K–$5M annually per large company",
      "Rules would take effect for fiscal years starting after December 2027",
    ],
    sources: [
      { name: "US Securities and Exchange Commission", url: "https://www.sec.gov/rules/proposed.shtml" },
      { name: "American Petroleum Institute", url: "https://www.api.org" },
    ],
  },
];
